import { useRouter } from 'next/router'

import GET_CREATORS from '@/graphql/queries/creators/getCreators'
import GET_PRODUCTS from '@/graphql/queries/products/getProducts'
import GET_PRODUCTS_COUNT from '@/graphql/queries/products/getProductsCount'
import GET_SEARCH_HIGHEST_PRICE from '@/graphql/queries/products/getSearchHighestPrice'
import Filters from '@/services/Filters/Filters'
import Creator from '@/ts/Creator'
import Aggregate from '@/ts/interfaces/global/Aggregate'
import ProductPrice from '@/ts/interfaces/product/ProductPrice'
import Product from '@/ts/Product'

import useHasura from './useHasura'
import usePagination from './usePagination'
import usePriceRange from './usePriceRange'

function useSearchProducts() {
  const {
    query: { creator: creator_nickname, q },
  } = useRouter()

  const search = `%%${q}%%`

  const currentFilters = Filters.get() || {}
  const priceRange = usePriceRange(100)

  const variables = {
    ...currentFilters,
    ...priceRange,
    creator_nickname,
    search,
  }

  const queryAggregate = useHasura<QueryAggregate>(
    'products_aggregate',
    GET_PRODUCTS_COUNT,
    { variables }
  )

  const count = queryAggregate.data?.products_aggregate?.aggregate?.count || 0

  const { limit, offset } = usePagination(count)
  const config = { enabled: count > 0 }

  const queryProducts = useHasura<QueryProducts>('products', GET_PRODUCTS, {
    config,
    variables: { ...variables, limit, offset, search },
  })

  const { data } = useHasura<QueryCreators>('creators', GET_CREATORS, {
    variables: { limit, offset, search },
  })

  const creators = data?.creators

  const queryPrice = useHasura<QueryPrice>(
    'products_highest_price',
    GET_SEARCH_HIGHEST_PRICE,
    { config, variables: { search } }
  )

  const isLoading = queryProducts.isFetching && queryProducts.isPreviousData

  const products = queryProducts.data?.products || []
  const maxPrice = Number(queryPrice.data?.prices?.[0]?.value) || 0

  return { products, creators, count, maxPrice, isLoading }
}

interface QueryProducts {
  products: Product[]
}

interface QueryCreators {
  creators: Creator[]
}

interface QueryPrice {
  prices: ProductPrice[]
}

interface QueryAggregate {
  products_aggregate: Aggregate
}

export default useSearchProducts
